import * as Api from '@ViewModels';
import { VmUtils } from '@ViewModels';
import { InfiniteData, QueryClient, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useLocalNotificationService } from '../../models/LocalNotifications';
import { Topics } from '../../models/LocalNotificationTopics';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { CALENDAR_ACTION_ITEMS_QUERY_KEY } from '../../web/components/calendar/hooks/calendarActionItemsQueryKey';
import { actionItemQueryKeys } from './actionItemQueryKeys';
import { IOpenActionItemsResult } from './useOpenActionItemsQuery';

type CompletedData = InfiniteData<Api.IPagedCollection<Api.IActionItem>>;

interface ICacheSnapshot {
	open: [readonly unknown[], IOpenActionItemsResult | undefined][];
	completed: [readonly unknown[], CompletedData | undefined][];
}

export interface IActionItemPatch {
	dueDate?: string | null;
	hasDueTime?: boolean;
	priority?: Api.ActionItemPriority;
	assignee?: Api.IUser | null;
}

export interface ICreateActionItemInput {
	content: Api.IRawRichTextContentState;
	plainText: string;
	dueDate: string | null;
	hasDueTime: boolean;
	priority: Api.ActionItemPriority | null;
	assignee: Api.IUser | null;
	contacts: Api.IContact[];
	companies?: Api.ICompany[];
	/** Defaults to the user's default visibility group when omitted. */
	visibility?: Api.ResourceVisibility;
}

function toReferencedEntities(input: ICreateActionItemInput): Api.IActionItem['referencedEntities'] {
	return {
		companies: (input.companies ?? []).map(company => ({
			entity: company,
			method: Api.RichContentReferenceMethod.Explicit,
		})),
		contacts: input.contacts.map(contact => ({
			entity: contact,
			method: Api.RichContentReferenceMethod.Explicit,
		})),
		users: [],
	};
}

export const OPTIMISTIC_ID_PREFIX = 'optimistic-';

export function isOptimisticActionItem(item: Pick<Api.IActionItem, 'id'>) {
	return !!item.id?.startsWith(OPTIMISTIC_ID_PREFIX);
}

function takeSnapshot(queryClient: QueryClient): ICacheSnapshot {
	return {
		open: queryClient.getQueriesData<IOpenActionItemsResult>({ queryKey: actionItemQueryKeys.openPrefix }),
		completed: queryClient.getQueriesData<CompletedData>({ queryKey: actionItemQueryKeys.completedPrefix }),
	};
}

function restoreSnapshot(queryClient: QueryClient, snapshot: ICacheSnapshot) {
	for (const [key, data] of snapshot.open) {
		queryClient.setQueryData(key, data);
	}
	for (const [key, data] of snapshot.completed) {
		queryClient.setQueryData(key, data);
	}
}

function patchOpenItems(queryClient: QueryClient, update: (items: Api.IActionItem[]) => Api.IActionItem[]) {
	queryClient.setQueriesData<IOpenActionItemsResult>({ queryKey: actionItemQueryKeys.openPrefix }, old =>
		old ? { ...old, items: update(old.items) } : old
	);
}

function patchCompletedItems(queryClient: QueryClient, update: (items: Api.IActionItem[]) => Api.IActionItem[]) {
	queryClient.setQueriesData<CompletedData>({ queryKey: actionItemQueryKeys.completedPrefix }, old =>
		old ? { ...old, pages: old.pages.map(page => ({ ...page, values: update(page.values ?? []) })) } : old
	);
}

function applyPatch(item: Api.IActionItem, patch: IActionItemPatch): Api.IActionItem {
	const next: Api.IActionItem = { ...item };
	if (patch.dueDate !== undefined) {
		next.dueDate = patch.dueDate ?? undefined;
	}
	if (patch.hasDueTime !== undefined) {
		next.hasDueTime = patch.hasDueTime;
	}
	if (patch.priority !== undefined) {
		next.priority = patch.priority;
	}
	if (patch.assignee !== undefined) {
		next.assignee = patch.assignee ?? undefined;
	}
	return next;
}

function toApiError(error: unknown): Api.IOperationResultNoValue {
	return (error as Api.IOperationResultNoValue) ?? { success: false };
}

/**
 * Every write to an action item from the new Action Items surfaces. Each mutation updates the open
 * and completed caches optimistically, rolls back on failure, and re-syncs the calendar, summary,
 * and legacy MobX lists afterwards.
 */
export function useActionItemMutations({ impersonationContext }: { impersonationContext?: Api.IImpersonationContext }) {
	const userSession = useUserSession();
	const queryClient = useQueryClient();
	const { postNotification } = useLocalNotificationService<Api.IActionItem>();

	const invalidateAll = () => {
		queryClient.invalidateQueries({ queryKey: actionItemQueryKeys.all });
		queryClient.invalidateQueries({ queryKey: [CALENDAR_ACTION_ITEMS_QUERY_KEY] });
	};

	const callApi = <T>(urlPath: string, method: 'GET' | 'POST' | 'PUT' | 'DELETE', body?: unknown) =>
		userSession.webServiceHelper.callAsync<T>(
			Api.ImpersonationBroker.composeApiUrl({ urlPath, impersonationContext }),
			method,
			body === undefined ? null : body
		);

	const update = useMutation<
		Api.IActionItem,
		unknown,
		{ item: Api.IActionItem; patch: IActionItemPatch },
		ICacheSnapshot
	>({
		mutationFn: ({ item, patch }) => {
			const body = applyPatch(item, patch);
			return callApi<Api.IActionItem>(`actionItem/${encodeURIComponent(item.id)}`, 'PUT', body);
		},
		onMutate: ({ item, patch }) => {
			const snapshot = takeSnapshot(queryClient);
			const patched = applyPatch(item, patch);
			patchOpenItems(queryClient, items => items.map(x => (x.id === item.id ? patched : x)));
			patchCompletedItems(queryClient, items => items.map(x => (x.id === item.id ? patched : x)));
			return snapshot;
		},
		onError: (error, _variables, snapshot) => {
			if (snapshot) {
				restoreSnapshot(queryClient, snapshot);
			}
			toast.error(toApiError(error).systemMessage || 'Failed to update action item');
		},
		onSuccess: saved => {
			postNotification({ info: saved, topic: Topics.EDIT_ACTION_ITEM });
		},
		onSettled: invalidateAll,
	});

	const toggleComplete = useMutation<
		Api.IActionItem,
		unknown,
		{ item: Api.IActionItem; isCompleted: boolean },
		ICacheSnapshot
	>({
		mutationFn: ({ item, isCompleted }) =>
			callApi<Api.IActionItem>(
				`actionItem/${encodeURIComponent(item.id)}/${isCompleted ? 'complete' : 'incomplete'}`,
				'POST'
			),
		onMutate: ({ item, isCompleted }) => {
			const snapshot = takeSnapshot(queryClient);
			if (isCompleted) {
				patchOpenItems(queryClient, items => items.filter(x => x.id !== item.id));
			} else {
				const reopened: Api.IActionItem = { ...item, isCompleted: false, completionDate: undefined };
				patchCompletedItems(queryClient, items => items.filter(x => x.id !== item.id));
				patchOpenItems(queryClient, items => (items.some(x => x.id === item.id) ? items : [reopened, ...items]));
			}
			return snapshot;
		},
		onError: (error, _variables, snapshot) => {
			if (snapshot) {
				restoreSnapshot(queryClient, snapshot);
			}
			toast.error(toApiError(error).systemMessage || 'Failed to update action item');
		},
		onSuccess: saved => {
			postNotification({ info: saved, topic: Topics.EDIT_ACTION_ITEM });
		},
		onSettled: invalidateAll,
	});

	const remove = useMutation<void, unknown, { item: Api.IActionItem }, ICacheSnapshot>({
		mutationFn: async ({ item }) => {
			await callApi<unknown>(`actionItem/${encodeURIComponent(item.id)}`, 'DELETE');
		},
		onMutate: ({ item }) => {
			const snapshot = takeSnapshot(queryClient);
			patchOpenItems(queryClient, items => items.filter(x => x.id !== item.id));
			patchCompletedItems(queryClient, items => items.filter(x => x.id !== item.id));
			return snapshot;
		},
		onError: (error, _variables, snapshot) => {
			if (snapshot) {
				restoreSnapshot(queryClient, snapshot);
			}
			toast.error(toApiError(error).systemMessage || 'Failed to delete action item');
		},
		onSuccess: (_result, { item }) => {
			postNotification({ info: item, topic: Topics.DELETE_ACTION_ITEM });
		},
		onSettled: invalidateAll,
	});

	const create = useMutation<
		Api.IActionItem,
		unknown,
		ICreateActionItemInput,
		{ snapshot: ICacheSnapshot; tempId: string }
	>({
		mutationFn: input => {
			const body: Api.IActionItem = {
				content: input.content,
				dueDate: input.dueDate ?? undefined,
				hasDueTime: input.hasDueTime,
				priority: input.priority ?? 'None',
				assignee: input.assignee ? { id: input.assignee.id } : undefined,
				referencedEntities: toReferencedEntities(input),
				visibility: input.visibility ?? VmUtils.getDefaultVisibility(userSession.user),
			};
			return callApi<Api.IActionItem>('actionItem', 'POST', body);
		},
		onMutate: input => {
			const snapshot = takeSnapshot(queryClient);
			const tempId = `${OPTIMISTIC_ID_PREFIX}${Date.now()}-${Math.random().toString(36).slice(2)}`;
			const optimistic: Api.IActionItem = {
				id: tempId,
				plainTextContent: input.plainText,
				preview: input.plainText.split('\n')[0],
				dueDate: input.dueDate ?? undefined,
				hasDueTime: input.hasDueTime,
				priority: input.priority ?? 'None',
				assignee: input.assignee ?? userSession.user ?? undefined,
				creationDate: new Date().toISOString(),
				isCompleted: false,
				referencedEntities: toReferencedEntities(input),
			};
			patchOpenItems(queryClient, items => [optimistic, ...items]);
			return { snapshot, tempId };
		},
		onError: (error, _input, context) => {
			if (context) {
				restoreSnapshot(queryClient, context.snapshot);
			}
			toast.error(toApiError(error).systemMessage || 'Failed to create action item');
		},
		onSuccess: (saved, _input, context) => {
			patchOpenItems(queryClient, items => items.map(x => (x.id === context?.tempId ? saved : x)));
			postNotification({ info: saved, topic: Topics.CREATE_ACTION_ITEM });
		},
		onSettled: invalidateAll,
	});

	const bulk = useMutation<
		Api.IBulkActionItemResult,
		unknown,
		Api.IBulkActionItemRequest & { assignee?: Api.IUser },
		ICacheSnapshot
	>({
		mutationFn: async ({ assignee: _assignee, ...request }) => {
			// The bulk envelope carries succeeded and failed ids at the top level and reports success=false
			// whenever any item failed, so read the body directly instead of relying on callAsync.
			const result = (await userSession.webServiceHelper.callWebServiceAsync<string>(
				Api.ImpersonationBroker.composeApiUrl({ urlPath: 'actionItem/bulk', impersonationContext }),
				'POST',
				request
			)) as unknown as Api.IBulkActionItemResult;
			if (Array.isArray(result.succeeded) || Array.isArray(result.failed)) {
				return result;
			}
			throw result;
		},
		onMutate: request => {
			const snapshot = takeSnapshot(queryClient);
			const ids = new Set(request.ids);
			switch (request.operation) {
				case 'Complete':
				case 'Delete':
					patchOpenItems(queryClient, items => items.filter(x => !ids.has(x.id)));
					if (request.operation === 'Delete') {
						patchCompletedItems(queryClient, items => items.filter(x => !ids.has(x.id)));
					}
					break;
				case 'Incomplete':
					patchCompletedItems(queryClient, items => items.filter(x => !ids.has(x.id)));
					break;
				case 'Reschedule':
					patchOpenItems(queryClient, items =>
						items.map(x =>
							ids.has(x.id)
								? applyPatch(x, { dueDate: request.dueDate ?? null, hasDueTime: request.hasDueTime ?? false })
								: x
						)
					);
					break;
				case 'Reassign':
					patchOpenItems(queryClient, items =>
						items.map(x =>
							ids.has(x.id) ? applyPatch(x, { assignee: request.assignee ?? { id: request.assigneeId } }) : x
						)
					);
					break;
				case 'SetPriority':
					patchOpenItems(queryClient, items =>
						items.map(x => (ids.has(x.id) && request.priority ? applyPatch(x, { priority: request.priority }) : x))
					);
					break;
				default:
					break;
			}
			return snapshot;
		},
		onError: (error, _variables, snapshot) => {
			if (snapshot) {
				restoreSnapshot(queryClient, snapshot);
			}
			toast.error(toApiError(error).systemMessage || 'Bulk update failed');
		},
		onSuccess: (result, request) => {
			const attempted = request.ids.length;
			const succeeded = result.succeeded?.length ?? 0;
			if (result.failed?.length) {
				toast.warning(`${succeeded} of ${attempted} action items updated`);
			}
		},
		onSettled: invalidateAll,
	});

	return { update, toggleComplete, remove, create, bulk };
}

export type ActionItemMutations = ReturnType<typeof useActionItemMutations>;
